// Exports the Complete stage (closed breakdowns) to CSV — the parsed-column
// pattern from exportHistoryLog: BOM-prefixed UTF-8, machine-parseable ISO
// timestamps, quote-escaped cells, date-stamped filename. Nothing is ever
// deleted in this module, so the export IS the retrieval story alongside search.

import { humanDuration } from "@/components/maintenance/lib";

const CSV_QUOTE = (val) => {
  if (val === null || val === undefined) return "";
  const s = String(val);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

// "2026-08-20 14:30:00" — Excel/Sheets read this as a real datetime AND
// reformat it to the reader's locale on open.
const fmtIso = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const hours = (s) => (s === null || s === undefined ? "" : Math.round((Number(s) / 3600) * 100) / 100);
const pauseText = (t) => (t.pauses || [])
  .map((p) => `${p.reason || "paused"}${p.to ? "" : " (still waiting)"}`)
  .join(" | ");

const COLUMNS = [
  { h: "Breakdown",        a: (t) => t.id },
  { h: "Machine",          a: (t) => t.machine?.name },
  { h: "Machine No",       a: (t) => t.machine_no },
  { h: "OEM",              a: (t) => t.machine?.make },
  { h: "Category",         a: (t) => t.machine?.category },
  { h: "Area",             a: (t) => t.machine?.area },
  { h: "Problem",          a: (t) => t.reason },
  { h: "Note",             a: (t) => t.note },
  { h: "Stopped",          a: (t) => (t.stoppage === "stopped" ? "Yes" : "No") },
  { h: "Reported By",      a: (t) => t.reported_by?.name },
  { h: "Reported At",      a: (t) => fmtIso(t.reported_at) },
  { h: "Taken Up By",      a: (t) => t.taken_by?.name },
  { h: "Taken Up At",      a: (t) => fmtIso(t.taken_at) },
  { h: "Repaired By",      a: (t) => t.repaired_by?.name },
  { h: "Repaired At",      a: (t) => fmtIso(t.repaired_at) },
  { h: "What Was Done",    a: (t) => t.root_note },
  { h: "Parts Used",       a: (t) => t.parts_note },
  { h: "Waiting For",      a: (t) => pauseText(t) },
  { h: "Send-backs",       a: (t) => t.sendback_count || 0 },
  { h: "Send-back Reasons", a: (t) => (t.sendbacks || []).map((s) => s.reason).join(" | ") },
  { h: "Closed By",        a: (t) => t.closed_by?.name },
  { h: "Closed At",        a: (t) => fmtIso(t.closed_at) },
  // Both shapes: hours for maths, the human string for reading.
  { h: "Response (h)",     a: (t) => hours(t.segments?.response) },
  { h: "Repair (h)",       a: (t) => hours(t.segments?.repair) },
  { h: "Waiting Spares (h)", a: (t) => hours(t.segments?.paused) },
  { h: "Sign-off Lag (h)", a: (t) => hours(t.segments?.verification) },
  { h: "Total Downtime (h)", a: (t) => hours(t.down_seconds) },
  { h: "Total Downtime",   a: (t) => humanDuration(t.down_seconds) },
  { h: "Rate (INR/hr)",    a: (t) => (t.rate_snapshot_inr ?? "") },
  { h: "Downtime Cost (INR)", a: (t) => (t.cost_inr ?? "") },
];

export function exportBreakdowns(tickets = []) {
  const headerRow = COLUMNS.map((c) => CSV_QUOTE(c.h)).join(",");
  const dataRows = tickets.map((t) => COLUMNS.map((c) => CSV_QUOTE(c.a(t) ?? "")).join(","));
  const csv = "\uFEFF" + [headerRow, ...dataRows].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const now = new Date();
  const stamp = `${String(now.getDate()).padStart(2,"0")}-${months[now.getMonth()]}-${String(now.getFullYear()).slice(-2)}`;
  const filename = `rocklink_breakdowns_${stamp}.csv`;

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { rowCount: tickets.length, filename };
}
